<template>
  <div class="file-types-settings">
    <v-container fluid>
      <!-- Page Header -->
      <div class="d-flex justify-space-between align-center mb-6">
        <div>
          <h1 class="text-h4 font-weight-bold mb-2">أنواع المرفقات</h1>
          <p class="text-body-1 text-grey">إدارة أنواع الملفات المسموحة في النظام</p>
        </div>
      </div>

      <!-- Content Card -->
      <v-card>
        <v-card-text class="pa-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <h2 class="text-h5">قائمة أنواع المرفقات</h2>
            <v-btn
            v-if="hasPermission('admin.file-types.store')"
              v-action="{ module: 'file-types', action: 'store' }"
              color="primary"
              prepend-icon="mdi-plus"
              @click="openAddFileTypeDialog"
            >
              إضافة نوع جديد
            </v-btn>
          </div>

          <v-data-table
            :headers="fileTypeHeaders"
            :items="fileTypes"
            :search="fileTypeSearch"
            :loading="loading"
            color="primary"
            first-icon="mdi-chevron-left"
            last-icon="mdi-chevron-right"
            next-icon="mdi-chevron-right"
            prev-icon="mdi-chevron-left"
            show-current-page
            show-first-last-page
            show-first-page
            show-last-page
            show-next-page
            show-previous-page
            class="elevation-1"
          >
            <template #top>
              <v-text-field
                v-model="fileTypeSearch"
                prepend-inner-icon="mdi-magnify"
                label="بحث في أنواع المرفقات"
                variant="outlined"
                density="compact"
                class="mx-4 mt-4"
              ></v-text-field>
            </template>

            <template v-slot:[`item.extension`]="{ item }">
              <v-chip color="primary" size="small" variant="outlined">
                .{{ item.extension }}
              </v-chip>
            </template>

            <template v-slot:[`item.max_size`]="{ item }">
              {{ formatFileSize(item.max_size) }}
            </template>

            <template v-slot:[`item.icon`]="{ item }">
              <v-icon :color="item.color || 'primary'" size="small">
                {{ item.icon || 'mdi-file' }}
              </v-icon>
            </template>

            <template v-slot:[`item.active`]="{ item }">
              <v-chip :color="item.active ? 'success' : 'error'" size="small" variant="tonal">
                {{ item.active ? 'مسموح' : 'غير مسموح' }}
              </v-chip>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                v-if="hasPermission('admin.file-types.update')"
                v-action="{ module: 'file-types', action: 'update' }"
                icon
                size="small"
                color="primary"
                variant="text"
                @click="editFileType(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                v-if="hasPermission('admin.file-types.destroy')"
                v-action="{ module: 'file-types', action: 'destroy' }"
                icon
                size="small"
                color="error"
                variant="text"
                @click="confirmDelete(item, 'fileType')"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- File Type Dialog -->
    <v-dialog v-model="showFileTypeDialog" max-width="500px">
      <v-card>
        <v-card-title>
          {{ editingFileType ? 'تعديل نوع المرفق' : 'إضافة نوع مرفق جديد' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="fileTypeForm" v-model="valid" @submit.prevent="saveFileType">
            <v-text-field
              v-model="fileTypeForm.name"
              label="اسم النوع"
              variant="outlined"
              required
              :rules="[v => !!v || 'اسم النوع مطلوب']"
              class="mb-4"
              placeholder="مستندات PDF، صور..."
            ></v-text-field>
            
            <v-text-field
              v-model="fileTypeForm.extension"
              label="امتداد الملف"
              variant="outlined"
              required
              :rules="[v => !!v || 'امتداد الملف مطلوب']"
              class="mb-4"
              placeholder="pdf, jpg, docx"
              hint="بدون نقطة في البداية"
              persistent-hint
            ></v-text-field>
            
            <v-text-field
              v-model="fileTypeForm.max_size"
              label="الحد الأقصى للحجم (بايت)"
              type="number"
              variant="outlined"
              required
              :rules="[v => !!v || 'الحد الأقصى مطلوب', v => v > 0 || 'الحجم يجب أن يكون أكبر من صفر']"
              class="mb-4"
              hint="مثال: 10485760 = 10 ميجابايت"
              persistent-hint
            ></v-text-field>

            <v-row class="mb-4">
              <v-col cols="8">
                <v-select
                  v-model="fileTypeForm.icon"
                  label="أيقونة الملف"
                  variant="outlined"
                  required
                  :rules="[v => !!v || 'الأيقونة مطلوبة']"
                  :items="iconOptions"
                  item-title="name"
                  item-value="value"
                  hint="اختر أيقونة مناسبة لنوع الملف"
                  persistent-hint
                >
                  <template v-slot:prepend-inner>
                    <v-icon :color="fileTypeForm.color">{{ fileTypeForm.icon }}</v-icon>
                  </template>
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props" :title="item.raw.name">
                      <template v-slot:prepend>
                        <v-icon :color="fileTypeForm.color" class="me-3">{{ item.raw.value }}</v-icon>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model="fileTypeForm.color"
                  label="اللون"
                  variant="outlined"
                  type="color"
                  required
                  :rules="[v => !!v || 'اللون مطلوب']"
                  class="color-picker"
                ></v-text-field>
              </v-col>
            </v-row>
            
            <v-textarea
              v-model="fileTypeForm.desc"
              label="الوصف"
              variant="outlined"
              rows="3"
              class="mb-4"
            ></v-textarea>
            
            <v-switch
              v-model="fileTypeForm.active"
              label="مسموح"
              color="success"
              class="mb-4"
            ></v-switch>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showFileTypeDialog = false">إلغاء</v-btn>
          <v-btn color="primary" @click="saveFileType">حفظ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">تأكيد الحذف</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف نوع المرفق؟
          <br><strong>{{ itemToDelete?.name }} (.{{ itemToDelete?.extension }})</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">إلغاء</v-btn>
          <v-btn color="error" @click="confirmDeleteAction">حذف</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import fileTypeService from '@/services/fileTypeService'
import { hasPermission, hasModule } from '@/utils/permissions'

export default {
  name: 'FileTypesView',
  data() {
    return {
      loading: false,
      valid: false,
      fileTypeSearch: '',
      showFileTypeDialog: false,
      editingFileType: null,
      fileTypes: [],
      fileTypeForm: {
        name: '',
        extension: '',
        max_size: 10485760, // 10MB default
        icon: 'mdi-file',
        color: '#1976D2',
        desc: '',
        active: true
      },
      fileTypeHeaders: [
        { title: 'اسم النوع', key: 'name', align: 'start' },
        { title: 'الامتداد', key: 'extension', align: 'center' },
        { title: 'الحد الأقصى', key: 'max_size', align: 'center' },
        { title: 'الأيقونة', key: 'icon', align: 'center' },
        { title: 'الوصف', key: 'desc', align: 'start' },
        { title: 'الحالة', key: 'active', align: 'center' },
        { title: 'الإجراءات', key: 'actions', align: 'center', sortable: false }
      ],
      deleteDialog: false,
      itemToDelete: null,
      deleteType: '',
      iconOptions: [
        // Document types
        { name: 'ملف عام', value: 'mdi-file' },
        { name: 'مستند PDF', value: 'mdi-file-pdf-box' },
        { name: 'مستند Word', value: 'mdi-file-word-box' },
        { name: 'جدول Excel', value: 'mdi-file-excel-box' },
        { name: 'عرض PowerPoint', value: 'mdi-file-powerpoint-box' },
        { name: 'مستند نصي', value: 'mdi-file-document' },
        
        // Image types
        { name: 'صورة عامة', value: 'mdi-file-image' },
        { name: 'صورة JPEG', value: 'mdi-file-jpg-box' },
        { name: 'صورة PNG', value: 'mdi-file-png-box' },
        { name: 'صورة GIF', value: 'mdi-file-gif-box' },
        { name: 'صورة SVG', value: 'mdi-svg' },
        
        // Video types
        { name: 'فيديو عام', value: 'mdi-file-video' },
        { name: 'فيديو MP4', value: 'mdi-file-video-outline' },
        { name: 'فيديو AVI', value: 'mdi-filmstrip' },
        
        // Audio types
        { name: 'صوت عام', value: 'mdi-file-music' },
        { name: 'ملف MP3', value: 'mdi-music-box' },
        { name: 'ملف WAV', value: 'mdi-waveform' },
        
        // Archive types
        { name: 'ملف مضغوط ZIP', value: 'mdi-zip-box' },
        { name: 'ملف RAR', value: 'mdi-archive' },
        { name: 'ملف 7Z', value: 'mdi-folder-zip' },
        
        // Code/Development
        { name: 'كود HTML', value: 'mdi-language-html5' },
        { name: 'كود CSS', value: 'mdi-language-css3' },
        { name: 'كود JavaScript', value: 'mdi-language-javascript' },
        { name: 'كود JSON', value: 'mdi-code-json' },
        { name: 'كود XML', value: 'mdi-xml' },
        
        // Other common types
        { name: 'ملف تنفيذي', value: 'mdi-application' },
        { name: 'خط', value: 'mdi-format-font' },
        { name: 'قاعدة بيانات', value: 'mdi-database' },
        { name: 'ملف CAD', value: 'mdi-file-cad-box' },
        { name: 'شهادة', value: 'mdi-certificate' },
        { name: 'مفتاح', value: 'mdi-key' }
      ]
    }
  },
  async mounted() {
    await this.loadFileTypes()
  },
  methods: {
    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },
    async loadFileTypes() {
      this.loading = true
      try {
        const response = await fileTypeService.getFileTypes()
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.fileTypes = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          this.fileTypes = response.data
        } else {
          this.fileTypes = []
        }
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في جلب أنواع المرفقات'
        })
      } finally {
        this.loading = false
      }
    },

    openAddFileTypeDialog() {
      this.editingFileType = null
      this.fileTypeForm = {
        name: '',
        extension: '',
        max_size: 10485760,
        icon: 'mdi-file',
        color: '#1976D2',
        desc: '',
        active: true
      }
      this.showFileTypeDialog = true
    },

    editFileType(fileType) {
      this.editingFileType = fileType
      this.fileTypeForm = {
        name: fileType.name,
        extension: fileType.extension,
        max_size: fileType.max_size,
        icon: fileType.icon || 'mdi-file',
        color: fileType.color || '#1976D2',
        desc: fileType.desc,
        active: Boolean(fileType.active)
      }
      this.showFileTypeDialog = true
    },

    async saveFileType() {
      if (!this.$refs.fileTypeForm.validate()) return

      this.loading = true
      try {
        // Prepare data according to API specification
        const fileTypeData = {
          name: this.fileTypeForm.name,
          extension: this.fileTypeForm.extension,
          max_size: this.fileTypeForm.max_size,
          icon: this.fileTypeForm.icon,
          color: this.fileTypeForm.color,
          desc: this.fileTypeForm.desc,
          active: this.fileTypeForm.active ? 1 : 0
        }

        if (this.editingFileType) {
          await fileTypeService.updateFileType(this.editingFileType.id, fileTypeData)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم تحديث نوع المرفق بنجاح'
          })
        } else {
          await fileTypeService.createFileType(fileTypeData)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم إضافة نوع المرفق بنجاح'
          })
        }
        
        this.showFileTypeDialog = false
        await this.loadFileTypes()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حفظ نوع المرفق'
        })
      } finally {
        this.loading = false
      }
    },

    confirmDelete(item, type) {
      this.itemToDelete = item
      this.deleteType = type
      this.deleteDialog = true
    },

    async confirmDeleteAction() {
      if (!this.itemToDelete) return

      this.loading = true
      try {
        await fileTypeService.deleteFileType(this.itemToDelete.id)
        this.$store.dispatch('alerts/showAlert', {
          type: 'success',
          message: 'تم حذف نوع المرفق بنجاح'
        })
        
        this.deleteDialog = false
        await this.loadFileTypes()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حذف نوع المرفق'
        })
      } finally {
        this.loading = false
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
.file-types-settings {
  min-height: 100vh;
}

.color-picker :deep(input[type="color"]) {
  width: 100%;
  height: 40px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.color-picker :deep(.v-field__input) {
  padding: 0;
  min-height: 40px;
}
</style> 